import {Component, Input, OnChanges} from '@angular/core';
import {
  AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup,
  ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import {MessageService} from 'primeng/api';

export type InventoryFeeFieldKey = 'upfit_1' | 'upfit_2' | 'dealer_adjustment' | 'fee_1' | 'fee_2';

export interface InventoryFeeSourceOption
{
  label: string;
  value: InventoryFeeFieldKey;
}

export const inventoryFeeSourceOptions: ReadonlyArray<InventoryFeeSourceOption> = [
  {label: 'Upfit 1', value: 'upfit_1'},
  {label: 'Upfit 2', value: 'upfit_2'},
  {label: 'Dealer Adjustment', value: 'dealer_adjustment'},
  {label: 'Fee 1', value: 'fee_1'},
  {label: 'Fee 2', value: 'fee_2'}
];

interface DynamicFeeOption
{
  label: string;
  value: string;
}

interface FeeAmountSourceOption
{
  label: string;
  value: string | null;
}

@Component({
  selector: 'auto-dynamic-fees-table',
  templateUrl: './dynamic-fees-table.component.html',
  styleUrls: ['./dynamic-fees-table.component.scss'],
  standalone: false
})
export class DynamicFeesTableComponent implements OnChanges
{
  @Input() feesConfigurationFormGroup = new UntypedFormGroup({});
  @Input() categoryName = '';
  @Input() purchaseFeeDisplayEnabled = false;
  @Input() vinSpecificFeesEnabled = false;
  @Input() inventoryFeeMappingsLoaded = false;
  @Input() mappedInventoryFeeSources: ReadonlyArray<InventoryFeeSourceOption> = [];

  feeTypeOptions: DynamicFeeOption[] = [
    {value: 'documentFee', label: 'Document Fee'},
    {value: 'licenseFee', label: 'License Fee'},
    {value: 'titleFee', label: 'Title Fee'},
    {value: 'acquisitionFee', label: 'Acquisition Fee'},
    {value: 'taxes', label: 'Taxes'},
    {value: 'dispositionFee', label: 'Disposition Fee'},
    {value: 'customFee', label: 'Other / Custom'}
  ];
  purchaseBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInSalePrice', label: 'Included in Price'},
    {value: 'addToSalePrice', label: 'Add to Price'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  readonly purchaseDisplayPositionOptions: DynamicFeeOption[] = [
    {value: 'abovePurchaseSubtotal', label: 'Above Purchase Subtotal'},
    {value: 'belowPurchaseSubtotal', label: 'Below Purchase Subtotal'}
  ];
  private readonly defaultPurchaseDisplayPosition = 'belowPurchaseSubtotal';
  paymentBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInPayment', label: 'Add to Selling Price'},
    {value: 'dueAtSigning', label: 'Due at Signing'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  financeBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInPayment', label: 'Add to Selling Price'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  readonly feeTitleMaxLength = 255;

  private readonly customFeeType = 'customFee';
  private readonly taxesFeeType = 'taxes';
  private readonly documentFeeType = 'documentFee';
  private readonly purchaseBehaviorNotUsed = 'notUsed';
  private readonly paymentBehaviorIncludeInPayment = 'includeInPayment';
  private readonly inventoryFeeTypeOptions: DynamicFeeOption[] = [
    {value: 'upfit', label: 'Upfit'},
    {value: 'dealerAdjustment', label: 'Dealer Adjustment'}
  ];
  private readonly duplicateCategoryNotificationLife = 7000;
  private duplicateFeeCategories = new Set<string>();
  private readonly inventorySourceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    this.validateInventorySources(control)

  /**
   * Uses the existing form and toast services for row editing and nonblocking category feedback.
   */
  constructor(private formBuilder: UntypedFormBuilder, private messageService: MessageService)
  {
  }

  /**
   * Reapplies row state when the form or asynchronously loaded feature eligibility changes.
   */
  ngOnChanges(): void
  {
    this.initializeFeeRows();
    this.configureInventorySourceValidation();
    this.notifyDuplicateFeeCategories();
  }

  /**
   * Keeps the shared Used editor independent of New purchase positioning.
   */
  get purchaseDisplayPositionAvailable(): boolean
  {
    const available = this.categoryName === 'New' && this.purchaseFeeDisplayEnabled;

    return available;
  }

  /**
   * Activates a legacy-disabled New row when purchase usage is selected, preserving its saved position.
   */
  handlePurchaseBehaviorChange(index: number): void
  {
    const feeRow = this.getFeeRow(index);
    const purchaseBehavior = this.getRowControlValue(feeRow, 'purchaseBehavior');
    const usesPurchaseFee = this.purchaseBehaviorOptions.some(option =>
      option.value === purchaseBehavior && option.value !== this.purchaseBehaviorNotUsed
    );
    if (this.purchaseDisplayPositionAvailable && usesPurchaseFee) {
      feeRow.get('enabled')?.setValue(true);
    }
  }

  /**
   * Get Fee Rows
   */
  get feeRows(): UntypedFormArray
  {
    return this.feesConfigurationFormGroup.get('rows') as UntypedFormArray;
  }

  /**
   * Adds a manual row while leaving all existing treatment defaults intact.
   */
  addFeeRow(): void
  {
    const feeRow = this.buildFeeRowFormGroup();

    this.feeRows.push(feeRow);
    this.syncSortOrder();
    this.applyRowState(feeRow);
    this.feesConfigurationFormGroup.markAsDirty();
    this.notifyDuplicateFeeCategories();
  }

  /**
   * Retains the protected Document Fee row and releases a removed row's source selection.
   */
  removeFeeRow(index: number): void
  {
    const feeRow = this.getFeeRow(index);

    if (!this.isDefaultDocumentFeeRow(feeRow)) {
      this.feeRows.removeAt(index);
      this.syncSortOrder();
      this.feesConfigurationFormGroup.markAsDirty();
      this.notifyDuplicateFeeCategories();
    }
  }

  /**
   * Reapplies category-specific validation without changing the row's title or application controls.
   */
  handleFeeTypeChange(index: number): void
  {
    const feeRow = this.getFeeRow(index);

    this.applyRowState(feeRow);
    this.feesConfigurationFormGroup.markAsDirty();
    this.notifyDuplicateFeeCategories();
  }

  /**
   * Switching amount sources never carries an entered amount into a VIN-backed configuration.
   */
  handleInventorySourceChange(index: number): void
  {
    const feeRow = this.getFeeRow(index);
    if (this.hasInventorySource(index)) {
      feeRow.get('value')?.setValue(null);
    }
    this.applyRowState(feeRow);
    this.feeRows.updateValueAndValidity();
    this.feesConfigurationFormGroup.markAsDirty();
  }

  /**
   * Keeps retained source rows distinct from entered amounts even if availability cannot be read.
   */
  hasInventorySource(index: number): boolean
  {
    const feeRow = this.getFeeRow(index);
    const hasSource = this.getRowControlValue(feeRow, 'inventoryFieldKey') !== '';

    return hasSource;
  }

  /**
   * Allows a retained missing source to be cleared without offering unmapped sources to other rows.
   */
  canChooseInventorySource(index: number): boolean
  {
    const feeRow = this.getFeeRow(index);
    const canChoose = this.vinSpecificFeesEnabled && this.inventoryFeeMappingsLoaded
      && this.getRowControlValue(feeRow, 'type') !== this.taxesFeeType
      && (this.mappedInventoryFeeSources.length > 0 || this.hasInventorySource(index));

    return canChoose;
  }

  /**
   * Failed or incomplete mapping reads must never be presented as mapping removal.
   */
  isInventoryMappingMissing(index: number): boolean
  {
    const feeRow = this.getFeeRow(index);
    const source = this.getRowControlValue(feeRow, 'inventoryFieldKey');
    const missing = this.vinSpecificFeesEnabled && this.inventoryFeeMappingsLoaded && source !== ''
      && !this.mappedInventoryFeeSources.some(option => option.value === source);

    return missing;
  }

  /**
   * Associates table-level validation with only the source rows that need correction.
   */
  getInventorySourceErrorIds(index: number): string[]
  {
    const errorIds: string[] = [];
    const feeRow = this.getFeeRow(index);
    const source = this.getRowControlValue(feeRow, 'inventoryFieldKey');
    if (this.vinSpecificFeesEnabled && source !== '') {
      const otherSources = this.getSelectedInventorySources(index);
      if (this.feeRows.hasError('duplicateInventorySource') && otherSources.has(source)) {
        errorIds.push(`fee-source-duplicate-${this.categoryName}`);
      }
      if (this.feeRows.hasError('inventorySourceTaxes')
        && this.getRowControlValue(feeRow, 'type') === this.taxesFeeType) {
        errorIds.push(`fee-source-taxes-${this.categoryName}`);
      }
    }

    return errorIds;
  }

  /**
   * Describes a missing mapping without marking the retained, saveable configuration invalid.
   */
  getInventorySourceDescription(index: number, errorIds: string[]): string | null
  {
    const descriptionIds = [...errorIds];
    if (this.isInventoryMappingMissing(index)) {
      descriptionIds.push(`fee-source-missing-${this.categoryName}-${index}`);
    }
    let description: string | null = null;
    if (descriptionIds.length > 0) {
      description = descriptionIds.join(' ');
    }

    return description;
  }

  /**
   * Shows canonical source names without attempting to resolve a VIN-specific amount in settings.
   */
  getInventorySourceLabel(index: number): string
  {
    const feeRow = this.getFeeRow(index);
    const source = this.getRowControlValue(feeRow, 'inventoryFieldKey');
    const sourceOption = inventoryFeeSourceOptions.find(option => option.value === source);
    const label = sourceOption?.label ?? source;

    return label;
  }

  /**
   * Sources are unique per table while the row's existing selection remains visible for correction.
   */
  getInventorySourceOptions(index: number): FeeAmountSourceOption[]
  {
    const feeRow = this.getFeeRow(index);
    const source = this.getRowControlValue(feeRow, 'inventoryFieldKey');
    const otherSources = this.getSelectedInventorySources(index);
    const options: FeeAmountSourceOption[] = [{label: 'Entered Amount', value: null}];
    this.mappedInventoryFeeSources.forEach(option =>
    {
      if (!otherSources.has(option.value) || option.value === source) {
        options.push(option);
      }
    });
    if (source !== '' && !options.some(option => option.value === source)) {
      options.push({label: this.getInventorySourceLabel(index), value: source});
    }

    return options;
  }

  /**
   * New categories follow the flag while saved manual categories retain their labels after rollback.
   */
  getFeeTypeOptions(index: number): DynamicFeeOption[]
  {
    const feeRow = this.getFeeRow(index);
    const currentType = this.getRowControlValue(feeRow, 'type');
    let options = [...this.feeTypeOptions];
    this.inventoryFeeTypeOptions.forEach(option =>
    {
      if (this.vinSpecificFeesEnabled || option.value === currentType) {
        options.push(option);
      }
    });
    if (this.vinSpecificFeesEnabled && this.hasInventorySource(index)) {
      options = options.filter(option => option.value !== this.taxesFeeType);
    }

    return options;
  }

  /**
   * @param index
   */
  canRemoveRow(index: number): boolean
  {
    let canRemoveRow = true;
    const feeRow = this.getFeeRow(index);

    if (this.isDefaultDocumentFeeRow(feeRow)) {
      canRemoveRow = false;
    }

    return canRemoveRow;
  }

  /**
   * @param index
   */
  getValueLabel(index: number): string
  {
    let valueLabel = 'Value ($)';
    const feeRow = this.getFeeRow(index);
    const feeType = this.getRowControlValue(feeRow, 'type');

    if (feeType === this.taxesFeeType) {
      valueLabel = 'Value (%)';
    }

    return valueLabel;
  }

  trackByIndex(index: number): number
  {
    return index;
  }

  /**
   * Defaults a newly added row to Below while Purchase starts unused.
   */
  private buildFeeRowFormGroup(): UntypedFormGroup
  {
    const accountBusinessCategoryGuid = this.getAccountBusinessCategoryGuid();
    const defaultFeeType = this.getDefaultFeeType();

    return this.formBuilder.group({
      id: new UntypedFormControl(null),
      type: new UntypedFormControl(defaultFeeType),
      customFeeName: new UntypedFormControl(null),
      value: new UntypedFormControl(null),
      inventoryFieldKey: new UntypedFormControl(null),
      isPercent: new UntypedFormControl(false),
      purchaseBehavior: new UntypedFormControl(this.purchaseBehaviorNotUsed),
      purchaseDisplayName: new UntypedFormControl(null),
      purchaseDisplayPosition: new UntypedFormControl(this.defaultPurchaseDisplayPosition),
      leaseBehavior: new UntypedFormControl(this.paymentBehaviorIncludeInPayment),
      financeBehavior: new UntypedFormControl(this.paymentBehaviorIncludeInPayment),
      isDefaultDocumentFee: new UntypedFormControl(false),
      sortOrder: new UntypedFormControl(this.feeRows.length),
      enabled: new UntypedFormControl(true),
      accountBusinessCategoryGuid: new UntypedFormControl(accountBusinessCategoryGuid)
    }) as UntypedFormGroup;
  }

  /**
   * @private
   */
  private getDefaultFeeType(): string
  {
    let defaultFeeType = this.customFeeType;

    this.feeTypeOptions.forEach((feeTypeOption) => {
      if (defaultFeeType === this.customFeeType && feeTypeOption.value !== this.customFeeType) {
        defaultFeeType = feeTypeOption.value;
      }
    });

    return defaultFeeType;
  }

  /**
   * @private
   */
  private initializeFeeRows(): void
  {
    this.feeRows.controls.forEach((feeRowControl) => {
      const feeRow = feeRowControl as UntypedFormGroup;

      this.applyRowState(feeRow);
    });
  }

  /**
   * @private
   */
  private getAccountBusinessCategoryGuid(): string | null
  {
    let accountBusinessCategoryGuid = null;
    const documentFeeGroup = this.feesConfigurationFormGroup.get('documentFee') as UntypedFormGroup | null;

    if (documentFeeGroup !== null) {
      const guidValue = documentFeeGroup.get('accountBusinessCategoryGuid')?.value;

      if (typeof guidValue === 'string' && guidValue !== '') {
        accountBusinessCategoryGuid = guidValue;
      }
    }

    return accountBusinessCategoryGuid;
  }

  /**
   * @private
   */
  private syncSortOrder(): void
  {
    this.feeRows.controls.forEach((feeRowControl, index) => {
      const feeRow = feeRowControl as UntypedFormGroup;

      feeRow.get('sortOrder')?.setValue(index, {emitEvent: false});
    });
  }

  /**
   * Applies row eligibility without clearing saved titles, amounts, or purchase positions.
   */
  private applyRowState(feeRow: UntypedFormGroup): void
  {
    this.applyDocumentFeeProtection(feeRow);
    this.applyPurchaseDisplayNameValidation(feeRow);
    this.applyPercentState(feeRow);
  }

  /**
   * Attaches only the feature's own validator, leaving existing manual validation unchanged while OFF.
   */
  private configureInventorySourceValidation(): void
  {
    this.feeRows.removeValidators(this.inventorySourceValidator);
    if (this.vinSpecificFeesEnabled) {
      this.feeRows.addValidators(this.inventorySourceValidator);
    }
    this.feeRows.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Includes disabled and default rows because source identity belongs to the whole configuration.
   */
  private validateInventorySources(control: AbstractControl): ValidationErrors | null
  {
    let errors: ValidationErrors | null = null;
    if (this.vinSpecificFeesEnabled && control instanceof UntypedFormArray) {
      const selectedSources = new Set<string>();
      control.controls.forEach(row =>
      {
        const source = this.getRowControlValue(row as UntypedFormGroup, 'inventoryFieldKey');
        if (source !== '') {
          if (selectedSources.has(source)) {
            errors = {...errors, duplicateInventorySource: true};
          }
          if (this.getRowControlValue(row as UntypedFormGroup, 'type') === this.taxesFeeType) {
            errors = {...errors, inventorySourceTaxes: true};
          }
          selectedSources.add(source);
        }
      });
    }

    return errors;
  }

  /**
   * Keeps selections in one vehicle-condition table independent of the other table.
   */
  private getSelectedInventorySources(excludedIndex: number): Set<string>
  {
    const sources = new Set<string>();
    this.feeRows.controls.forEach((control, index) =>
    {
      if (index !== excludedIndex) {
        const source = this.getRowControlValue(control as UntypedFormGroup, 'inventoryFieldKey');
        if (source !== '') {
          sources.add(source);
        }
      }
    });

    return sources;
  }

  /**
   * Duplicate categories are permitted; notify once for newly duplicated categories without blocking saves.
   */
  private notifyDuplicateFeeCategories(): void
  {
    const duplicateCategories = this.getDuplicateFeeCategories();
    const newDuplicates = [...duplicateCategories].filter(category => !this.duplicateFeeCategories.has(category));
    this.duplicateFeeCategories = duplicateCategories;
    if (newDuplicates.length > 0) {
      const options = [...this.feeTypeOptions, ...this.inventoryFeeTypeOptions];
      const labels = newDuplicates.map(category => options.find(option => option.value === category)?.label ?? category);
      this.messageService.add({
        severity: 'warn',
        summary: `${this.categoryName} Fees: Duplicate categories`,
        detail: `Multiple rows use these fee categories: ${labels.join(', ')}. Review their amounts and application before saving.`,
        life: this.duplicateCategoryNotificationLife
      });
    }
  }

  /**
   * Custom rows are intentionally repeatable and do not produce category warnings.
   */
  private getDuplicateFeeCategories(): Set<string>
  {
    const duplicates = new Set<string>();
    const selectedTypes = new Set<string>();
    if (this.vinSpecificFeesEnabled) {
      this.feeRows.controls.forEach(control =>
      {
        const feeType = this.getRowControlValue(control as UntypedFormGroup, 'type');
        if (feeType !== '' && feeType !== this.customFeeType) {
          if (selectedTypes.has(feeType)) {
            duplicates.add(feeType);
          }
          selectedTypes.add(feeType);
        }
      });
    }

    return duplicates;
  }

  /**
   * @param feeRow
   * @private
   */
  private applyPurchaseDisplayNameValidation(feeRow: UntypedFormGroup): void
  {
    const purchaseDisplayNameControl = feeRow.get('purchaseDisplayName') as UntypedFormControl;
    const feeType = this.getRowControlValue(feeRow, 'type');
    const titleValidators = [Validators.maxLength(this.feeTitleMaxLength)];

    if (feeType === this.customFeeType) {
      titleValidators.push(Validators.required, Validators.pattern(/\S/));
    }

    purchaseDisplayNameControl.setValidators(titleValidators);
    purchaseDisplayNameControl.updateValueAndValidity({emitEvent: false});
  }

  /**
   * @param feeRow
   * @private
   */
  private applyPercentState(feeRow: UntypedFormGroup): void
  {
    let isPercent = false;
    const feeType = this.getRowControlValue(feeRow, 'type');

    if (feeType === this.taxesFeeType) {
      isPercent = true;
    }

    feeRow.get('isPercent')?.setValue(isPercent, {emitEvent: false});
  }

  /**
   * @param feeRow
   * @private
   */
  private applyDocumentFeeProtection(feeRow: UntypedFormGroup): void
  {
    const typeControl = feeRow.get('type');
    const isDefaultDocumentFee = this.isDefaultDocumentFeeRow(feeRow);

    if (isDefaultDocumentFee) {
      typeControl?.setValue(this.documentFeeType, {emitEvent: false});
      typeControl?.disable({emitEvent: false});
      feeRow.get('enabled').setValue(true);
    }

    if (!isDefaultDocumentFee) {
      typeControl?.enable({emitEvent: false});
    }
  }

  /**
   * @param index
   * @private
   */
  private getFeeRow(index: number): UntypedFormGroup
  {
    return this.feeRows.at(index) as UntypedFormGroup;
  }

  /**
   * @param feeRow
   * @private
   */
  private isDefaultDocumentFeeRow(feeRow: UntypedFormGroup): boolean
  {
    let isDefaultDocumentFeeRow = false;
    const defaultFlag = feeRow.get('isDefaultDocumentFee')?.value;

    if (defaultFlag === true) {
      isDefaultDocumentFeeRow = true;
    }

    return isDefaultDocumentFeeRow;
  }

  /**
   * @param feeRow
   * @param controlName
   * @private
   */
  private getRowControlValue(feeRow: UntypedFormGroup, controlName: string): string
  {
    let controlValue = '';
    const rawValue = feeRow.get(controlName)?.value;

    if (typeof rawValue === 'string') {
      controlValue = rawValue;
    }

    return controlValue;
  }
}
